"use client";

import { useState } from "react";
import { apiFetch } from "@/src/lib/api-fetch";
import { ChevronDown, ChevronUp, Loader2, Plus, Trash2, Upload } from "lucide-react";
import {
  IMAGE_ALIGNS,
  IMAGE_SIZES,
  ROADMAP_COLORS,
  type BlockType,
  type ImageAlign,
  type ImageSize,
} from "@/lib/lesson-content/schema";
import { BLOCK_LABELS, type EditorBlock } from "./blockDefaults";
import MarkdownField from "./MarkdownField";
import { findPhrases, missingPhrases } from "@/lib/ile/phrase-highlight";
import { DecisionTreeEditor, PromptBuilderEditor, ScenarioSortEditor, SpotTheErrorsEditor } from "./ActivityEditors";
import { AddBtn, ColorSelect, Field, FieldGroup, RemoveBtn, inputClass, textareaClass } from "./formParts";

/**
 * Per-block form fields. Authors one block's `data`; the strict contract is only
 * enforced on save (via `safeParseLessonContent`), so these inputs stay loose.
 *
 * `updateData(index, patch)` shallow-merges `patch` into the block's `data`. The
 * caller decides what `index` means — the chain canvas passes a closure that
 * targets the selected block by id, so `index` is ignored there.
 */
export function BlockFields({
  block,
  index,
  updateData,
}: {
  block: EditorBlock;
  index: number;
  updateData: (i: number, patch: Record<string, unknown>) => void;
}) {
  const set = (patch: Record<string, unknown>) => updateData(index, patch);
  // Replace the whole data object: keys missing from `next` are cleared, not kept.
  const replaceData = (next: Record<string, unknown>) =>
    set({ ...Object.fromEntries(Object.keys(block.data).map((k) => [k, undefined])), ...next });

  switch (block.type) {
    case "rich_text": {
      const d = block.data as { markdown?: string };
      return (
        <FieldGroup label="Content">
          <MarkdownField value={d.markdown ?? ""} onChange={(v) => set({ markdown: v })} />
        </FieldGroup>
      );
    }

    case "image": {
      const d = block.data as {
        image_url?: string;
        alt?: string;
        caption?: string;
        size?: ImageSize;
        align?: ImageAlign;
      };
      return (
        <>
          <Field label="Image URL">
            <ImageUrlField value={d.image_url ?? ""} onChange={(url) => set({ image_url: url })} />
          </Field>
          <div className="flex flex-wrap gap-4">
            <FieldGroup label="Size">
              <SegmentedControl
                options={IMAGE_SIZES}
                labels={IMAGE_SIZE_LABELS}
                value={d.size ?? "full"}
                onChange={(size) => set({ size })}
              />
            </FieldGroup>
            <FieldGroup label="Alignment">
              <SegmentedControl
                options={IMAGE_ALIGNS}
                labels={IMAGE_ALIGN_LABELS}
                value={d.align ?? "center"}
                onChange={(align) => set({ align })}
              />
            </FieldGroup>
          </div>
          <Field label="Alt text">
            <input value={d.alt ?? ""} onChange={(e) => set({ alt: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Caption (optional)">
            <input value={d.caption ?? ""} onChange={(e) => set({ caption: e.target.value })} className={inputClass} />
          </Field>
        </>
      );
    }

    case "mcq": {
      const d = block.data as {
        question?: string;
        options?: string[];
        correct_index?: number;
        correct_indices?: number[];
        points?: number;
        explanation?: string;
      };
      const options = d.options ?? [];
      const multi = Array.isArray(d.correct_indices);
      const keys = multi ? (d.correct_indices ?? []) : [d.correct_index ?? 0];
      // `undefined` drops the other key on save, so exactly one answer form is stored.
      const setKeys = (next: number[]) =>
        set(
          multi
            ? { correct_indices: next.length ? next : [0], correct_index: undefined }
            : { correct_index: next[0] ?? 0, correct_indices: undefined },
        );
      const setOption = (oi: number, value: string) =>
        set({ options: options.map((o, idx) => (idx === oi ? value : o)) });
      const addOption = () => set({ options: [...options, ""] });
      const removeOption = (oi: number) => {
        const next = options.filter((_, idx) => idx !== oi);
        const shifted = keys.filter((k) => k !== oi).map((k) => (k > oi ? k - 1 : k));
        const fallback = [Math.min(keys[0] ?? 0, next.length - 1)];
        set({
          options: next,
          ...(multi
            ? { correct_indices: shifted.length ? shifted : fallback }
            : { correct_index: shifted[0] ?? fallback[0] }),
        });
      };
      const toggleKey = (oi: number) =>
        setKeys(multi ? (keys.includes(oi) ? keys.filter((k) => k !== oi) : [...keys, oi].sort((x, y) => x - y)) : [oi]);
      const switchMode = (toMulti: boolean) =>
        set(
          toMulti
            ? { correct_indices: keys, correct_index: undefined }
            : { correct_index: keys[0] ?? 0, correct_indices: undefined },
        );

      return (
        <>
          <Field label="Question">
            <input value={d.question ?? ""} onChange={(e) => set({ question: e.target.value })} className={inputClass} />
          </Field>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
            <input type="checkbox" checked={multi} onChange={(e) => switchMode(e.target.checked)} />
            Several correct answers (learners see &ldquo;Select all that apply&rdquo;)
          </label>
          <FieldGroup label={multi ? "Options (tick every correct one)" : "Options (select the correct one)"}>
            <div className="space-y-2">
              {options.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input
                    type={multi ? "checkbox" : "radio"}
                    name={`mcq-correct-${block.id}`}
                    checked={keys.includes(oi)}
                    onChange={() => toggleKey(oi)}
                    title="Mark as correct answer"
                  />
                  <input value={opt} onChange={(e) => setOption(oi, e.target.value)} className={inputClass} placeholder={`Option ${oi + 1}`} />
                  <button type="button" onClick={() => removeOption(oi)} disabled={options.length <= 2} title="Remove option" className="rounded p-1.5 text-rose-500 hover:bg-rose-50 disabled:opacity-30">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addOption} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900">
                <Plus className="h-3.5 w-3.5" /> Add option
              </button>
            </div>
          </FieldGroup>
          <div className="flex gap-4">
            <Field label="Points">
              <input
                type="number"
                min={0}
                value={d.points ?? 0}
                onChange={(e) => set({ points: Number(e.target.value) })}
                className={`${inputClass} w-24`}
              />
            </Field>
          </div>
          <Field label="Explanation (optional)">
            <input value={d.explanation ?? ""} onChange={(e) => set({ explanation: e.target.value })} className={inputClass} />
          </Field>
        </>
      );
    }

    case "image_hotspot": {
      const d = block.data as {
        image_url?: string;
        alt?: string;
        mode?: "explore" | "find";
        hotspots?: { id: string; x: number; y: number; title: string; info: string }[];
      };
      const hs = d.hotspots ?? [];
      const setHs = (next: typeof hs) => set({ hotspots: next });
      return (
        <>
          <Field label="Image URL">
            <ImageUrlField value={d.image_url ?? ""} onChange={(url) => set({ image_url: url })} />
          </Field>
          <Field label="Alt text (optional)">
            <input value={d.alt ?? ""} onChange={(e) => set({ alt: e.target.value })} className={inputClass} />
          </Field>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={d.mode === "find"}
              onChange={(e) => set({ mode: e.target.checked ? "find" : undefined })}
            />
            Find the hidden spots (pins start hidden; learners click the image to find each one)
          </label>
          {d.image_url ? (
            <Field label="Click the image to drop a hotspot">
              <div
                className="relative inline-block max-w-full cursor-crosshair overflow-hidden rounded-lg border border-slate-200"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  if (!rect.width || !rect.height) return;
                  const x = Math.min(100, Math.max(0, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
                  const y = Math.min(100, Math.max(0, Math.round(((e.clientY - rect.top) / rect.height) * 100)));
                  setHs([...hs, { id: crypto.randomUUID(), x, y, title: "", info: "" }]);
                }}
              >
                <img src={d.image_url} alt={d.alt ?? ""} draggable={false} className="block max-h-72 w-auto select-none" />
                {hs.map((h, i) => (
                  <span
                    key={h.id}
                    style={{ left: `${h.x}%`, top: `${h.y}%` }}
                    className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold text-white ring-2 ring-white"
                  >
                    {i + 1}
                  </span>
                ))}
              </div>
            </Field>
          ) : null}
          <Field label="Hotspots (x / y are % of the image)">
            <div className="space-y-2">
              {hs.map((h, i) => (
                <div key={h.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500">Pin {i + 1}</span>
                    <label className="flex items-center gap-1 text-[11px] text-slate-400">
                      x%
                      <input type="number" min={0} max={100} value={h.x} onChange={(e) => setHs(hs.map((x, xi) => (xi === i ? { ...x, x: Number(e.target.value) } : x)))} className={`${inputClass} w-16`} />
                    </label>
                    <label className="flex items-center gap-1 text-[11px] text-slate-400">
                      y%
                      <input type="number" min={0} max={100} value={h.y} onChange={(e) => setHs(hs.map((x, xi) => (xi === i ? { ...x, y: Number(e.target.value) } : x)))} className={`${inputClass} w-16`} />
                    </label>
                    <span className="flex-1" />
                    <RemoveBtn onClick={() => setHs(hs.filter((_, xi) => xi !== i))} />
                  </div>
                  {/* Title + info are the popup shown when this pin is clicked. */}
                  <div className="space-y-1.5 rounded-md bg-slate-50 p-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Shown when clicked</p>
                    <input value={h.title} onChange={(e) => setHs(hs.map((x, xi) => (xi === i ? { ...x, title: e.target.value } : x)))} className={inputClass} placeholder="Title (heading)" />
                    <input value={h.info} onChange={(e) => setHs(hs.map((x, xi) => (xi === i ? { ...x, info: e.target.value } : x)))} className={inputClass} placeholder="Info (body text)" />
                  </div>
                </div>
              ))}
              <AddBtn label="Add hotspot" onClick={() => setHs([...hs, { id: crypto.randomUUID(), x: 50, y: 50, title: "", info: "" }])} />
            </div>
          </Field>
        </>
      );
    }

    case "flip_cards": {
      const d = block.data as { cards?: { id: string; front: string; back: string }[] };
      const cards = d.cards ?? [];
      const setCards = (next: typeof cards) => set({ cards: next });
      return (
        <Field label="Cards (front / back)">
          <div className="space-y-2">
            {cards.map((c, i) => (
              <div key={c.id} className="flex items-center gap-2">
                <input value={c.front} onChange={(e) => setCards(cards.map((x, xi) => (xi === i ? { ...x, front: e.target.value } : x)))} className={inputClass} placeholder="Front" />
                <input value={c.back} onChange={(e) => setCards(cards.map((x, xi) => (xi === i ? { ...x, back: e.target.value } : x)))} className={inputClass} placeholder="Back" />
                <RemoveBtn onClick={() => setCards(cards.filter((_, xi) => xi !== i))} />
              </div>
            ))}
            <AddBtn label="Add card" onClick={() => setCards([...cards, { id: crypto.randomUUID(), front: "", back: "" }])} />
          </div>
        </Field>
      );
    }

    case "accordion_tabs": {
      const d = block.data as { display?: string; sections?: { id: string; title: string; body: string }[] };
      const sections = d.sections ?? [];
      const setSec = (next: typeof sections) => set({ sections: next });
      const moveSec = (i: number, dir: -1 | 1) => {
        const next = [...sections];
        [next[i], next[i + dir]] = [next[i + dir], next[i]];
        setSec(next);
      };
      return (
        <>
          <Field label="Display">
            <select value={d.display ?? "accordion"} onChange={(e) => set({ display: e.target.value })} className={inputClass}>
              <option value="accordion">Accordion</option>
              <option value="tabs">Tabs</option>
            </select>
          </Field>
          <FieldGroup label="Sections">
            <div className="space-y-2">
              {sections.map((s, i) => (
                <div key={s.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <span className="w-5 shrink-0 text-center text-xs font-bold text-slate-400">{i + 1}</span>
                    <input value={s.title} onChange={(e) => setSec(sections.map((x, xi) => (xi === i ? { ...x, title: e.target.value } : x)))} className={inputClass} placeholder="Section title" />
                    <button type="button" onClick={() => moveSec(i, -1)} disabled={i === 0} title="Move up" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => moveSec(i, 1)} disabled={i === sections.length - 1} title="Move down" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                    <RemoveBtn onClick={() => setSec(sections.filter((_, xi) => xi !== i))} disabled={sections.length <= 1} />
                  </div>
                  <MarkdownField value={s.body} onChange={(v) => setSec(sections.map((x, xi) => (xi === i ? { ...x, body: v } : x)))} rows={5} />
                </div>
              ))}
              <AddBtn label="Add section" onClick={() => setSec([...sections, { id: crypto.randomUUID(), title: "", body: "" }])} />
            </div>
          </FieldGroup>
        </>
      );
    }

    case "stepped_timeline": {
      type StepImage = { image_url: string; alt: string; caption?: string };
      const d = block.data as { steps?: { id: string; label: string; body: string; images?: StepImage[] }[] };
      const steps = d.steps ?? [];
      const setSteps = (next: typeof steps) => set({ steps: next });
      const setImages = (i: number, next: StepImage[]) =>
        setSteps(steps.map((x, xi) => (xi === i ? { ...x, images: next.length ? next : undefined } : x)));
      return (
        <Field label="Steps">
          <div className="space-y-2">
            {steps.map((s, i) => (
              <div key={s.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-400">{i + 1}.</span>
                  <input value={s.label} onChange={(e) => setSteps(steps.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder="Step label" />
                  <RemoveBtn onClick={() => setSteps(steps.filter((_, xi) => xi !== i))} />
                </div>
                <textarea value={s.body} onChange={(e) => setSteps(steps.map((x, xi) => (xi === i ? { ...x, body: e.target.value } : x)))} rows={2} className={textareaClass} placeholder="Step detail (markdown)" />
                {(s.images ?? []).map((img, ii) => {
                  const imgs = s.images ?? [];
                  const patchImg = (p: Partial<StepImage>) => setImages(i, imgs.map((x, xj) => (xj === ii ? { ...x, ...p } : x)));
                  return (
                    <div key={ii} className="space-y-2 rounded-md bg-slate-50 p-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500">Screenshot {ii + 1}</span>
                        <RemoveBtn onClick={() => setImages(i, imgs.filter((_, xj) => xj !== ii))} />
                      </div>
                      <ImageUrlField value={img.image_url ?? ""} onChange={(url) => patchImg({ image_url: url })} />
                      <input value={img.alt ?? ""} onChange={(e) => patchImg({ alt: e.target.value })} className={inputClass} placeholder="Alt text (describe the screenshot)" />
                      <input value={img.caption ?? ""} onChange={(e) => patchImg({ caption: e.target.value })} className={inputClass} placeholder="Caption (optional)" />
                    </div>
                  );
                })}
                {(s.images ?? []).length < 4 ? (
                  <AddBtn label="Add screenshot" onClick={() => setImages(i, [...(s.images ?? []), { image_url: "", alt: "", caption: "" }])} />
                ) : null}
              </div>
            ))}
            <AddBtn label="Add step" onClick={() => setSteps([...steps, { id: crypto.randomUUID(), label: "", body: "" }])} />
          </div>
        </Field>
      );
    }

    case "wheel_diagram": {
      const d = block.data as { center_label?: string; slices?: { id: string; label: string; body: string; color?: string }[] };
      const slices = d.slices ?? [];
      const setSlices = (next: typeof slices) => set({ slices: next });
      return (
        <>
          <Field label="Centre label (optional)">
            <input value={d.center_label ?? ""} onChange={(e) => set({ center_label: e.target.value })} className={inputClass} placeholder="e.g. The Verification Wheel" />
          </Field>
          <Field label="Slices (2–8)">
            <div className="space-y-2">
              {slices.map((s, i) => (
                <div key={s.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <input value={s.label} onChange={(e) => setSlices(slices.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder="Slice name (keep it short)" />
                    <ColorSelect value={s.color} autoLabel="Auto colour" onChange={(color) => setSlices(slices.map((x, xi) => (xi === i ? { ...x, color } : x)))} />
                    <RemoveBtn onClick={() => setSlices(slices.filter((_, xi) => xi !== i))} disabled={slices.length <= 2} />
                  </div>
                  <textarea value={s.body} onChange={(e) => setSlices(slices.map((x, xi) => (xi === i ? { ...x, body: e.target.value } : x)))} rows={2} className={textareaClass} placeholder="Slice text (markdown)" />
                </div>
              ))}
              {slices.length < 8 ? (
                <AddBtn label="Add slice" onClick={() => setSlices([...slices, { id: crypto.randomUUID(), label: "", body: "" }])} />
              ) : null}
            </div>
          </Field>
        </>
      );
    }

    case "nested_layers": {
      const d = block.data as { layers?: { id: string; label: string; body: string }[] };
      const layers = d.layers ?? [];
      const setLayers = (next: typeof layers) => set({ layers: next });
      const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= layers.length) return;
        const c = [...layers];
        [c[i], c[j]] = [c[j], c[i]];
        setLayers(c);
      };
      return (
        <Field label="Layers (2–6, outermost first)">
          <div className="space-y-2">
            {layers.map((l, i) => (
              <div key={l.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-400">{i + 1}.</span>
                  <input value={l.label} onChange={(e) => setLayers(layers.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder={i === 0 ? "Outermost layer name" : "Layer name"} />
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move out" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === layers.length - 1} title="Move in" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                  <RemoveBtn onClick={() => setLayers(layers.filter((_, xi) => xi !== i))} disabled={layers.length <= 2} />
                </div>
                <textarea value={l.body} onChange={(e) => setLayers(layers.map((x, xi) => (xi === i ? { ...x, body: e.target.value } : x)))} rows={2} className={textareaClass} placeholder="Layer text (markdown)" />
              </div>
            ))}
            {layers.length < 6 ? (
              <AddBtn label="Add inner layer" onClick={() => setLayers([...layers, { id: crypto.randomUUID(), label: "", body: "" }])} />
            ) : null}
          </div>
        </Field>
      );
    }

    case "add_next_layer": {
      const d = block.data as { button_label?: string; layers?: { id: string; label: string; body: string; color?: string }[] };
      const layers = d.layers ?? [];
      const setLayers = (next: typeof layers) => set({ layers: next });
      const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= layers.length) return;
        const c = [...layers];
        [c[i], c[j]] = [c[j], c[i]];
        setLayers(c);
      };
      return (
        <>
        <Field label="Button text (optional)">
          <input
            value={d.button_label ?? ""}
            maxLength={40}
            onChange={(e) => set({ button_label: e.target.value.trim() ? e.target.value : undefined })}
            className={inputClass}
            placeholder="Add the next layer"
          />
        </Field>
        <FieldGroup label="Layers (2–6, foundation first; learners add them one at a time)">
          <div className="space-y-2">
            {layers.map((l, i) => (
              <div key={l.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                <div className="flex items-center gap-2">
                  <span className="w-5 shrink-0 text-center text-xs font-bold text-slate-400">{i + 1}</span>
                  <input value={l.label} onChange={(e) => setLayers(layers.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder={i === 0 ? "Foundation, e.g. Language model" : "Layer name, e.g. Chatbot"} />
                  <ColorSelect value={l.color} autoLabel="Indigo shade" onChange={(color) => setLayers(layers.map((x, xi) => (xi === i ? { ...x, color } : x)))} />
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move down the stack" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === layers.length - 1} title="Move up the stack" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                  <RemoveBtn onClick={() => setLayers(layers.filter((_, xi) => xi !== i))} disabled={layers.length <= 2} />
                </div>
                <MarkdownField value={l.body} onChange={(v) => setLayers(layers.map((x, xi) => (xi === i ? { ...x, body: v } : x)))} rows={4} />
              </div>
            ))}
            {layers.length < 6 ? (
              <AddBtn label="Add layer" onClick={() => setLayers([...layers, { id: crypto.randomUUID(), label: "", body: "" }])} />
            ) : null}
          </div>
        </FieldGroup>
        </>
      );
    }

    case "checklist": {
      const d = block.data as { title?: string; items?: { id: string; label: string }[] };
      const items = d.items ?? [];
      const setItems = (next: typeof items) => set({ items: next });
      const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= items.length) return;
        const c = [...items];
        [c[i], c[j]] = [c[j], c[i]];
        setItems(c);
      };
      return (
        <>
          <Field label="Title (optional)">
            <input value={d.title ?? ""} onChange={(e) => set({ title: e.target.value || undefined })} className={inputClass} placeholder="e.g. Before you continue" />
          </Field>
          <Field label="Items (1–12; learners tick every one to finish)">
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={it.id} className="flex items-center gap-2">
                  <input value={it.label} onChange={(e) => setItems(items.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder="e.g. I've created my free account" />
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} title="Move down" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                  <RemoveBtn onClick={() => setItems(items.filter((_, xi) => xi !== i))} disabled={items.length <= 1} />
                </div>
              ))}
              {items.length < 12 ? (
                <AddBtn label="Add item" onClick={() => setItems([...items, { id: crypto.randomUUID(), label: "" }])} />
              ) : null}
            </div>
          </Field>
        </>
      );
    }

    case "free_response": {
      type FormField = { id: string; label: string; hint?: string; long?: boolean; optional?: boolean; options?: string[] };
      const d = block.data as {
        prompt?: string;
        placeholder?: string;
        min_words?: number;
        guidance?: string[];
        fields?: FormField[];
        entries?: number;
        min_entries?: number;
        entry_label?: string;
        preview?: Record<string, unknown>;
        output?: { heading?: string; template?: string; link?: { label?: string; url?: string } };
      };
      const guidance = d.guidance ?? [];
      const output = d.output;
      // Clearing every part removes the prompt altogether, so an emptied one doesn't fail validation.
      const setOutput = (patch: Partial<NonNullable<typeof d.output>>) => {
        const next = { ...(output ?? {}), ...patch };
        set({ output: next.heading || next.template || next.link ? next : undefined });
      };
      const setGuidance = (next: string[]) => set({ guidance: next.length ? next : undefined });
      const fields = d.fields;
      const newField = (): FormField => ({ id: crypto.randomUUID().slice(0, 8), label: "" });
      const setFields = (next: FormField[]) => set({ fields: next });
      const updateField = (fi: number, patch: Partial<FormField>) =>
        setFields((fields ?? []).map((f, i) => (i === fi ? { ...f, ...patch } : f)));
      const entries = d.entries ?? 1;
      const clampEntries = (v: number) => Math.max(1, Math.min(6, Number(v) || 1));
      return (
        <>
          <FieldGroup label="Question or task">
            <MarkdownField value={d.prompt ?? ""} onChange={(v) => set({ prompt: v })} rows={4} />
          </FieldGroup>
          <FieldGroup label="Answer type">
            <select
              value={fields ? "form" : "text"}
              onChange={(e) =>
                e.target.value === "form"
                  ? set({ fields: [newField()] })
                  : set({ fields: undefined, entries: undefined, min_entries: undefined, entry_label: undefined, preview: undefined, output: undefined })
              }
              className={inputClass}
            >
              <option value="text">One text box</option>
              <option value="form">Form with labelled fields (a lab)</option>
            </select>
          </FieldGroup>
          {fields ? (
            <>
              <FieldGroup label="Fields (up to 16; an entry counts as complete once every required field is filled)">
                <div className="space-y-2">
                  {fields.map((f, fi) => (
                    <div key={f.id} className="space-y-1.5 rounded-lg border border-slate-200 p-2.5">
                      <div className="flex items-center gap-2">
                        <input value={f.label} onChange={(e) => updateField(fi, { label: e.target.value })} className={inputClass} placeholder="Label, e.g. Where exactly is AI used?" />
                        <RemoveBtn onClick={() => fields.length > 1 && setFields(fields.filter((_, i) => i !== fi))} />
                      </div>
                      <input value={f.hint ?? ""} onChange={(e) => updateField(fi, { hint: e.target.value || undefined })} className={inputClass} placeholder="Hint (optional), e.g. Name the specific feature, not the whole app" />
                      <ChoicesInput options={f.options} onChange={(options) => updateField(fi, { options })} />
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                          <input type="checkbox" checked={!!f.long} onChange={(e) => updateField(fi, { long: e.target.checked || undefined })} />
                          Longer answer (multi-line box)
                        </label>
                        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                          <input type="checkbox" checked={!!f.optional} onChange={(e) => updateField(fi, { optional: e.target.checked || undefined })} />
                          Optional
                        </label>
                      </div>
                    </div>
                  ))}
                  {fields.length < 16 ? <AddBtn label="Add field" onClick={() => setFields([...fields, newField()])} /> : null}
                </div>
              </FieldGroup>
              <div className="flex flex-wrap gap-4">
                <FieldGroup label="Entries (repeats of the fields)">
                  <input
                    type="number"
                    min={1}
                    max={6}
                    value={entries}
                    onChange={(e) => {
                      const n = clampEntries(Number(e.target.value));
                      set({
                        entries: n === 1 ? undefined : n,
                        min_entries: d.min_entries && d.min_entries > n ? n : d.min_entries,
                        // A preview card and a filled-in prompt only work on a one-entry form.
                        ...(n > 1 ? { preview: undefined, output: undefined } : {}),
                      });
                    }}
                    className={`${inputClass} w-28`}
                  />
                </FieldGroup>
                {entries > 1 ? (
                  <>
                    <FieldGroup label="Entries needed to submit">
                      <input
                        type="number"
                        min={1}
                        max={entries}
                        value={d.min_entries ?? entries}
                        onChange={(e) => {
                          const n = Math.min(entries, clampEntries(Number(e.target.value)));
                          set({ min_entries: n === entries ? undefined : n });
                        }}
                        className={`${inputClass} w-28`}
                      />
                    </FieldGroup>
                    <FieldGroup label="Entry name">
                      <input value={d.entry_label ?? ""} onChange={(e) => set({ entry_label: e.target.value || undefined })} className={`${inputClass} w-44`} placeholder="e.g. System" />
                    </FieldGroup>
                  </>
                ) : null}
              </div>
              {entries === 1 ? (
                <JsonDataField
                  label="Live preview card (optional, JSON: heading, title_field, sections, footer…)"
                  rows={8}
                  data={(d.preview as Record<string, unknown> | undefined) ?? {}}
                  onChange={(next) => set({ preview: Object.keys(next).length ? next : undefined })}
                />
              ) : null}
              {entries === 1 ? (
                <FieldGroup label="Filled-in prompt (optional): the learner's answers drop into it as they type, and they copy it">
                  <div className="space-y-2">
                    <input
                      value={output?.heading ?? ""}
                      onChange={(e) => setOutput({ heading: e.target.value || undefined })}
                      className={inputClass}
                      placeholder="Heading, e.g. Your Gamma prompt"
                    />
                    <textarea
                      value={output?.template ?? ""}
                      onChange={(e) => setOutput({ template: e.target.value || undefined })}
                      rows={5}
                      className={inputClass}
                      placeholder="Prompt text. Use the buttons below to insert a field's answer."
                    />
                    <div className="flex flex-wrap gap-1.5">
                      {fields.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => setOutput({ template: `${output?.template ?? ""}{{${f.id}}}` })}
                          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          + {f.label || f.id}
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <input
                        value={output?.link?.label ?? ""}
                        onChange={(e) =>
                          setOutput({ link: e.target.value || output?.link?.url ? { ...output?.link, label: e.target.value || undefined } : undefined })
                        }
                        className={`${inputClass} w-44`}
                        placeholder="Link label, e.g. Open Gamma"
                      />
                      <input
                        value={output?.link?.url ?? ""}
                        onChange={(e) =>
                          setOutput({ link: e.target.value || output?.link?.label ? { ...output?.link, url: e.target.value || undefined } : undefined })
                        }
                        className={`${inputClass} flex-1`}
                        placeholder="Link URL (optional), e.g. https://gamma.app"
                      />
                    </div>
                  </div>
                </FieldGroup>
              ) : null}
            </>
          ) : null}
          {fields ? null : (
          <>
          <div className="flex flex-wrap gap-4">
            <FieldGroup label="Minimum words">
              <input
                type="number"
                min={1}
                max={500}
                value={d.min_words ?? 20}
                onChange={(e) => set({ min_words: Math.max(1, Math.min(500, Number(e.target.value) || 1)) })}
                className={`${inputClass} w-28`}
              />
            </FieldGroup>
          </div>
          <Field label="Placeholder (optional)">
            <input value={d.placeholder ?? ""} onChange={(e) => set({ placeholder: e.target.value || undefined })} className={inputClass} placeholder="e.g. Start with the system's name…" />
          </Field>
          </>
          )}
          <Field label="What a strong answer includes (optional, up to 8; shown to learners, and the rubric for AI feedback later)">
            <div className="space-y-2">
              {guidance.map((g, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={g} onChange={(e) => setGuidance(guidance.map((x, xi) => (xi === i ? e.target.value : x)))} className={inputClass} placeholder="e.g. Names a specific feature, not the whole app" />
                  <RemoveBtn onClick={() => setGuidance(guidance.filter((_, xi) => xi !== i))} />
                </div>
              ))}
              {guidance.length < 8 ? <AddBtn label="Add point" onClick={() => setGuidance([...guidance, ""])} /> : null}
            </div>
          </Field>
        </>
      );
    }

    case "format_switcher": {
      const d = block.data as {
        title?: string;
        prompt?: string;
        complete_on?: string;
        options?: { id: string; label: string; body: string }[];
      };
      const options = d.options ?? [];
      const setOptions = (next: typeof options) => set({ options: next });
      const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= options.length) return;
        const c = [...options];
        [c[i], c[j]] = [c[j], c[i]];
        setOptions(c);
      };
      return (
        <>
          <Field label="Title (optional)">
            <input value={d.title ?? ""} onChange={(e) => set({ title: e.target.value })} className={inputClass} placeholder="e.g. One answer, five formats" />
          </Field>
          <Field label="Prompt (optional)">
            <input value={d.prompt ?? ""} onChange={(e) => set({ prompt: e.target.value })} className={inputClass} placeholder="e.g. Pick a format to see the answer." />
          </Field>
          <Field label="Complete when">
            <select value={d.complete_on ?? "any"} onChange={(e) => set({ complete_on: e.target.value })} className={inputClass}>
              <option value="any">Any option is picked</option>
              <option value="all">Every option is opened</option>
            </select>
          </Field>
          <Field label="Options (2–6)">
            <div className="space-y-2">
              {options.map((o, i) => (
                <div key={o.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <input value={o.label} onChange={(e) => setOptions(options.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder="Button label (e.g. Table)" />
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === options.length - 1} title="Move down" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                    <RemoveBtn onClick={() => setOptions(options.filter((_, xi) => xi !== i))} disabled={options.length <= 2} />
                  </div>
                  <textarea value={o.body} onChange={(e) => setOptions(options.map((x, xi) => (xi === i ? { ...x, body: e.target.value } : x)))} rows={4} className={textareaClass} placeholder="Content shown for this option (markdown: text, list, table or ``` code)" />
                </div>
              ))}
              {options.length < 6 ? (
                <AddBtn label="Add option" onClick={() => setOptions([...options, { id: crypto.randomUUID(), label: "", body: "" }])} />
              ) : null}
            </div>
          </Field>
        </>
      );
    }

    case "image_switcher": {
      type Opt = { id: string; label: string; image_url: string; alt: string; caption?: string };
      const d = block.data as { options?: Opt[] };
      const options = d.options ?? [];
      const setOptions = (next: Opt[]) => set({ options: next });
      const patch = (i: number, p: Partial<Opt>) => setOptions(options.map((x, xi) => (xi === i ? { ...x, ...p } : x)));
      const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= options.length) return;
        const c = [...options];
        [c[i], c[j]] = [c[j], c[i]];
        setOptions(c);
      };
      return (
        <Field label="Images (2–6, first one shows by default)">
          <div className="space-y-2">
            {options.map((o, i) => (
              <div key={o.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-400">{i + 1}.</span>
                  <input value={o.label} onChange={(e) => patch(i, { label: e.target.value })} className={inputClass} placeholder="Button label (e.g. Before)" />
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === options.length - 1} title="Move down" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                  <RemoveBtn onClick={() => setOptions(options.filter((_, xi) => xi !== i))} disabled={options.length <= 2} />
                </div>
                <ImageUrlField value={o.image_url ?? ""} onChange={(url) => patch(i, { image_url: url })} />
                <input value={o.alt ?? ""} onChange={(e) => patch(i, { alt: e.target.value })} className={inputClass} placeholder="Alt text (describe the image)" />
                <input value={o.caption ?? ""} onChange={(e) => patch(i, { caption: e.target.value })} className={inputClass} placeholder="Caption (optional)" />
              </div>
            ))}
            {options.length < 6 ? (
              <AddBtn label="Add image" onClick={() => setOptions([...options, { id: crypto.randomUUID(), label: "", image_url: "", alt: "", caption: "" }])} />
            ) : null}
          </div>
        </Field>
      );
    }

    case "vertical_roadmap": {
      type Era = { id: string; name: string; color: string };
      type Ev = { id: string; date: string; era_id: string; text: string };
      const d = block.data as { eras?: Era[]; events?: Ev[] };
      const eras = d.eras ?? [];
      const events = d.events ?? [];
      const setEras = (next: Era[]) => set({ eras: next });
      const setEvents = (next: Ev[]) => set({ events: next });
      const patchEvent = (i: number, p: Partial<Ev>) => setEvents(events.map((x, xi) => (xi === i ? { ...x, ...p } : x)));
      const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= events.length) return;
        const c = [...events];
        [c[i], c[j]] = [c[j], c[i]];
        setEvents(c);
      };
      return (
        <>
          <Field label="Eras (1–6)">
            <div className="space-y-2">
              {eras.map((era, i) => (
                <div key={era.id} className="flex items-center gap-2">
                  <input value={era.name} onChange={(e) => setEras(eras.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))} className={inputClass} placeholder="Era name (e.g. Rules era)" />
                  <select value={era.color} onChange={(e) => setEras(eras.map((x, xi) => (xi === i ? { ...x, color: e.target.value } : x)))} className={`${inputClass} w-32`}>
                    {ROADMAP_COLORS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <RemoveBtn
                    onClick={() => {
                      // Remap events that pointed at the removed era to the first
                      // remaining one, so no event is left with a dangling era_id.
                      const remaining = eras.filter((_, xi) => xi !== i);
                      const fallback = remaining[0]?.id ?? "";
                      set({
                        eras: remaining,
                        events: events.map((ev) => (ev.era_id === era.id ? { ...ev, era_id: fallback } : ev)),
                      });
                    }}
                    disabled={eras.length <= 1}
                  />
                </div>
              ))}
              {eras.length < 6 ? (
                <AddBtn label="Add era" onClick={() => setEras([...eras, { id: crypto.randomUUID(), name: "", color: ROADMAP_COLORS[eras.length % ROADMAP_COLORS.length] }])} />
              ) : null}
            </div>
          </Field>
          <Field label="Events (2–30, top to bottom)">
            <div className="space-y-2">
              {events.map((ev, i) => (
                <div key={ev.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <input value={ev.date} onChange={(e) => patchEvent(i, { date: e.target.value })} className={`${inputClass} w-32`} placeholder="Date (e.g. 1956)" />
                    <select value={ev.era_id} onChange={(e) => patchEvent(i, { era_id: e.target.value })} className={inputClass}>
                      {eras.map((era) => (
                        <option key={era.id} value={era.id}>{era.name || "Untitled era"}</option>
                      ))}
                    </select>
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === events.length - 1} title="Move down" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                    <RemoveBtn onClick={() => setEvents(events.filter((_, xi) => xi !== i))} disabled={events.length <= 2} />
                  </div>
                  <textarea value={ev.text} onChange={(e) => patchEvent(i, { text: e.target.value })} rows={2} className={textareaClass} placeholder="What happened (markdown)" />
                </div>
              ))}
              {events.length < 30 ? (
                <AddBtn label="Add event" onClick={() => setEvents([...events, { id: crypto.randomUUID(), date: "", era_id: events[events.length - 1]?.era_id ?? eras[0]?.id ?? "", text: "" }])} />
              ) : null}
            </div>
          </Field>
        </>
      );
    }

    case "categorization": {
      const d = block.data as {
        buckets?: { id: string; label: string }[];
        items?: { id: string; label: string; correct_bucket_id: string }[];
        points_per_match?: number;
      };
      const buckets = d.buckets ?? [];
      const items = d.items ?? [];
      const setB = (next: typeof buckets) => set({ buckets: next });
      const setI = (next: typeof items) => set({ items: next });
      return (
        <>
          <Field label="Buckets (categories)">
            <div className="space-y-2">
              {buckets.map((b, i) => (
                <div key={b.id} className="flex items-center gap-2">
                  <input value={b.label} onChange={(e) => setB(buckets.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder={`Bucket ${i + 1}`} />
                  <RemoveBtn
                    onClick={() => {
                      // Remap items that pointed at the removed bucket to the first
                      // remaining one, so no item is left with a dangling correct_bucket_id.
                      const remaining = buckets.filter((_, xi) => xi !== i);
                      const fallback = remaining[0]?.id ?? "";
                      const remappedItems = items.map((it) =>
                        it.correct_bucket_id === b.id ? { ...it, correct_bucket_id: fallback } : it,
                      );
                      set({ buckets: remaining, items: remappedItems });
                    }}
                    disabled={buckets.length <= 2}
                  />
                </div>
              ))}
              <AddBtn label="Add bucket" onClick={() => setB([...buckets, { id: crypto.randomUUID(), label: "" }])} />
            </div>
          </Field>
          <Field label="Items (assign each to its correct bucket)">
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={it.id} className="flex items-center gap-2">
                  <input value={it.label} onChange={(e) => setI(items.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder={`Item ${i + 1}`} />
                  <select value={it.correct_bucket_id} onChange={(e) => setI(items.map((x, xi) => (xi === i ? { ...x, correct_bucket_id: e.target.value } : x)))} className={`${inputClass} w-44`}>
                    {buckets.map((b) => (
                      <option key={b.id} value={b.id}>{b.label || "Untitled bucket"}</option>
                    ))}
                  </select>
                  <RemoveBtn onClick={() => setI(items.filter((_, xi) => xi !== i))} />
                </div>
              ))}
              <AddBtn label="Add item" onClick={() => setI([...items, { id: crypto.randomUUID(), label: "", correct_bucket_id: buckets[0]?.id ?? "" }])} />
            </div>
          </Field>
          <Field label="Points per match">
            <input type="number" min={0} value={d.points_per_match ?? 0} onChange={(e) => set({ points_per_match: Number(e.target.value) })} className={`${inputClass} w-24`} />
          </Field>
        </>
      );
    }

    case "sequencing": {
      const d = block.data as { items?: { id: string; label: string }[]; points?: number };
      const items = d.items ?? [];
      const setItems = (next: typeof items) => set({ items: next, correct_order: next.map((x) => x.id) });
      const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= items.length) return;
        const c = [...items];
        [c[i], c[j]] = [c[j], c[i]];
        setItems(c);
      };
      return (
        <>
          <p className="text-xs text-slate-500">List items in the correct order — students see them shuffled.</p>
          <Field label="Items (top = first)">
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={it.id} className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-400">{i + 1}.</span>
                  <input value={it.label} onChange={(e) => setItems(items.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder={`Item ${i + 1}`} />
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} title="Move down" className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
                  <RemoveBtn onClick={() => setItems(items.filter((_, xi) => xi !== i))} disabled={items.length <= 2} />
                </div>
              ))}
              <AddBtn label="Add item" onClick={() => setItems([...items, { id: crypto.randomUUID(), label: "" }])} />
            </div>
          </Field>
          <Field label="Points">
            <input type="number" min={0} value={d.points ?? 0} onChange={(e) => set({ points: Number(e.target.value) })} className={`${inputClass} w-24`} />
          </Field>
        </>
      );
    }

    case "fill_blank": {
      const d = block.data as {
        template?: string;
        blanks?: { id: string; options: string[]; correct_index: number }[];
        points_per_blank?: number;
      };
      const blanks = d.blanks ?? [];
      const setBl = (next: typeof blanks) => set({ blanks: next });
      return (
        <>
          <Field label="Template (use {{id}} where each blank goes)">
            <textarea value={d.template ?? ""} onChange={(e) => set({ template: e.target.value })} rows={3} className={textareaClass} placeholder="GPUs use {{unit}} cores to {{action}} matrices." />
          </Field>
          <Field label="Blanks (id must match a token in the template)">
            <div className="space-y-2">
              {blanks.map((b, i) => {
                const opts = b.options ?? [];
                const setOpts = (next: string[]) =>
                  setBl(blanks.map((x, xi) => (xi === i ? { ...x, options: next, correct_index: b.correct_index >= next.length ? Math.max(0, next.length - 1) : b.correct_index } : x)));
                return (
                  <div key={i} className="space-y-2 rounded-md border border-slate-200 p-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">{"{{"}</span>
                      <input value={b.id} onChange={(e) => setBl(blanks.map((x, xi) => (xi === i ? { ...x, id: e.target.value } : x)))} className={`${inputClass} w-32`} placeholder="blank id" />
                      <span className="text-xs text-slate-400">{"}}"}</span>
                      <RemoveBtn onClick={() => setBl(blanks.filter((_, xi) => xi !== i))} />
                    </div>
                    <div className="space-y-1 pl-2">
                      {opts.map((o, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <input type="radio" name={`fb-${block.id}-${i}`} checked={(b.correct_index ?? 0) === oi} onChange={() => setBl(blanks.map((x, xi) => (xi === i ? { ...x, correct_index: oi } : x)))} title="Correct answer" />
                          <input value={o} onChange={(e) => setOpts(opts.map((x, xi) => (xi === oi ? e.target.value : x)))} className={inputClass} placeholder={`Option ${oi + 1}`} />
                          <RemoveBtn onClick={() => setOpts(opts.filter((_, xi) => xi !== oi))} disabled={opts.length <= 2} />
                        </div>
                      ))}
                      <AddBtn label="Add option" onClick={() => setOpts([...opts, ""])} />
                    </div>
                  </div>
                );
              })}
              <AddBtn
                label="Add blank"
                onClick={() => {
                  // Find the smallest unused blankN so a mid-list delete can't mint a duplicate id.
                  const used = new Set(blanks.map((b) => b.id));
                  let n = 1;
                  while (used.has(`blank${n}`)) n++;
                  setBl([...blanks, { id: `blank${n}`, options: ["", ""], correct_index: 0 }]);
                }}
              />
            </div>
          </Field>
          <Field label="Points per blank">
            <input type="number" min={0} value={d.points_per_blank ?? 0} onChange={(e) => set({ points_per_blank: Number(e.target.value) })} className={`${inputClass} w-24`} />
          </Field>
        </>
      );
    }

    case "dual_chatbot": {
      const d = block.data as {
        title?: string;
        subtitle?: string;
        presets?: string[];
        rules?: { id: string; pattern: string; reply: string }[];
        failure_reply?: string;
        canned_llm?: { question: string; reply: string }[];
        rule_panel_title?: string;
        rule_panel_sub?: string;
        llm_panel_title?: string;
        llm_panel_sub?: string;
        fine_print?: string;
        min_asks?: number;
      };
      const presets = d.presets ?? [];
      const rules = d.rules ?? [];
      const canned = d.canned_llm ?? [];
      return (
        <>
          <Field label="Title (optional)">
            <input value={d.title ?? ""} onChange={(e) => set({ title: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Subtitle (optional)">
            <textarea value={d.subtitle ?? ""} onChange={(e) => set({ subtitle: e.target.value })} rows={2} className={textareaClass} />
          </Field>
          <Field label="Min asks to complete">
            <input type="number" min={1} max={10} value={d.min_asks ?? 2} onChange={(e) => set({ min_asks: Number(e.target.value) })} className={`${inputClass} w-24`} />
          </Field>
          <Field label="Preset questions">
            <div className="space-y-2">
              {presets.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={p} onChange={(e) => set({ presets: presets.map((x, xi) => (xi === i ? e.target.value : x)) })} className={inputClass} />
                  <RemoveBtn onClick={() => set({ presets: presets.filter((_, xi) => xi !== i) })} disabled={presets.length <= 1} />
                </div>
              ))}
              <AddBtn label="Add preset" onClick={() => set({ presets: [...presets, ""] })} />
            </div>
          </Field>
          <Field label="Rule-based patterns">
            <div className="space-y-2">
              {rules.map((r, i) => (
                <div key={r.id} className="space-y-1 rounded border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <input value={r.pattern} onChange={(e) => set({ rules: rules.map((x, xi) => xi === i ? { ...x, pattern: e.target.value } : x) })} className={inputClass} placeholder="open|hours|time" />
                    <RemoveBtn onClick={() => set({ rules: rules.filter((_, xi) => xi !== i) })} disabled={rules.length <= 1} />
                  </div>
                  <input value={r.reply} onChange={(e) => set({ rules: rules.map((x, xi) => xi === i ? { ...x, reply: e.target.value } : x) })} className={inputClass} placeholder="Reply" />
                </div>
              ))}
              <AddBtn label="Add rule" onClick={() => set({ rules: [...rules, { id: crypto.randomUUID(), pattern: "", reply: "" }] })} />
            </div>
          </Field>
          <Field label="Failure reply (no rule matched)">
            <textarea value={d.failure_reply ?? ""} onChange={(e) => set({ failure_reply: e.target.value })} rows={2} className={textareaClass} />
          </Field>
          <Field label="Canned LLM replies (exact question → reply)">
            <div className="space-y-2">
              {canned.map((c, i) => (
                <div key={i} className="space-y-1 rounded border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <input value={c.question} onChange={(e) => set({ canned_llm: canned.map((x, xi) => xi === i ? { ...x, question: e.target.value } : x) })} className={inputClass} placeholder="exact question (any case)" />
                    <RemoveBtn onClick={() => set({ canned_llm: canned.filter((_, xi) => xi !== i) })} />
                  </div>
                  <textarea value={c.reply} onChange={(e) => set({ canned_llm: canned.map((x, xi) => xi === i ? { ...x, reply: e.target.value } : x) })} rows={2} className={textareaClass} />
                </div>
              ))}
              <AddBtn label="Add canned reply" onClick={() => set({ canned_llm: [...canned, { question: "", reply: "" }] })} />
            </div>
          </Field>
          <Field label="Fine print (optional)">
            <textarea value={d.fine_print ?? ""} onChange={(e) => set({ fine_print: e.target.value })} rows={2} className={textareaClass} />
          </Field>
        </>
      );
    }

    case "data_sandbox": {
      type Judgment = {
        id: string;
        question: string;
        ai_claim: string;
        ai_correct: boolean;
        truth: string;
      };
      type Dataset = {
        id: string;
        label: string;
        info?: string;
        default_metric?: string;
        columns: { id: string; label: string; numeric?: boolean }[];
        rows: Record<string, string | number>[];
        judgments: Judgment[];
      };
      const d = block.data as {
        title?: string;
        subtitle?: string;
        min_judgments?: number;
        datasets?: Dataset[];
      };
      const datasets = d.datasets ?? [];
      const setDatasets = (next: Dataset[]) => set({ datasets: next });
      const patchDataset = (i: number, patch: Partial<Dataset>) =>
        setDatasets(datasets.map((ds, di) => (di === i ? { ...ds, ...patch } : ds)));
      return (
        <>
          <Field label="Title (optional)">
            <input value={d.title ?? ""} onChange={(e) => set({ title: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Subtitle (optional)">
            <textarea value={d.subtitle ?? ""} onChange={(e) => set({ subtitle: e.target.value })} rows={2} className={textareaClass} />
          </Field>
          <Field label="Min judgments to complete">
            <input
              type="number"
              min={1}
              max={30}
              value={d.min_judgments ?? 6}
              onChange={(e) => set({ min_judgments: Number(e.target.value) })}
              className={`${inputClass} w-24`}
            />
          </Field>
          <Field label="Datasets (table/chart data + AI claim judgments)">
            <div className="space-y-3">
              {datasets.map((ds, di) => (
                <div key={ds.id} className="space-y-2 rounded border border-slate-200 p-3">
                  <div className="flex items-center gap-2">
                    <input
                      value={ds.label}
                      onChange={(e) => patchDataset(di, { label: e.target.value })}
                      className={inputClass}
                      placeholder="Dataset label"
                    />
                    <RemoveBtn
                      onClick={() => setDatasets(datasets.filter((_, xi) => xi !== di))}
                      disabled={datasets.length <= 1}
                    />
                  </div>
                  <textarea
                    value={ds.info ?? ""}
                    onChange={(e) => patchDataset(di, { info: e.target.value })}
                    rows={2}
                    className={textareaClass}
                    placeholder="Info shown above the chart/table"
                  />
                  <p className="text-xs text-slate-500">
                    {ds.columns.length} columns · {ds.rows.length} rows (edit via lesson JSON if you need to change the table numbers)
                  </p>
                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Judgments</div>
                    {(ds.judgments ?? []).map((j, ji) => (
                      <div key={j.id} className="space-y-1 rounded border border-slate-100 bg-slate-50 p-2">
                        <div className="flex items-center gap-2">
                          <input
                            value={j.question}
                            onChange={(e) => {
                              const judgments = ds.judgments.map((x, xi) =>
                                xi === ji ? { ...x, question: e.target.value } : x,
                              );
                              patchDataset(di, { judgments });
                            }}
                            className={inputClass}
                            placeholder="Question"
                          />
                          <RemoveBtn
                            onClick={() =>
                              patchDataset(di, {
                                judgments: ds.judgments.filter((_, xi) => xi !== ji),
                              })
                            }
                            disabled={ds.judgments.length <= 1}
                          />
                        </div>
                        <textarea
                          value={j.ai_claim}
                          onChange={(e) => {
                            const judgments = ds.judgments.map((x, xi) =>
                              xi === ji ? { ...x, ai_claim: e.target.value } : x,
                            );
                            patchDataset(di, { judgments });
                          }}
                          rows={2}
                          className={textareaClass}
                          placeholder="AI claim"
                        />
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={j.ai_correct}
                            onChange={(e) => {
                              const judgments = ds.judgments.map((x, xi) =>
                                xi === ji ? { ...x, ai_correct: e.target.checked } : x,
                              );
                              patchDataset(di, { judgments });
                            }}
                          />
                          AI claim is correct
                        </label>
                        <textarea
                          value={j.truth}
                          onChange={(e) => {
                            const judgments = ds.judgments.map((x, xi) =>
                              xi === ji ? { ...x, truth: e.target.value } : x,
                            );
                            patchDataset(di, { judgments });
                          }}
                          rows={2}
                          className={textareaClass}
                          placeholder="True answer revealed after judging"
                        />
                      </div>
                    ))}
                    <AddBtn
                      label="Add judgment"
                      onClick={() =>
                        patchDataset(di, {
                          judgments: [
                            ...ds.judgments,
                            {
                              id: crypto.randomUUID(),
                              question: "",
                              ai_claim: "",
                              ai_correct: false,
                              truth: "",
                            },
                          ],
                        })
                      }
                    />
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-500">
                To add another dataset with its own rows, paste it into the lesson JSON (or duplicate this block and edit).
              </p>
            </div>
          </Field>
        </>
      );
    }

    case "agent_simulator": {
      const d = block.data as {
        title?: string;
        goal?: string;
        inbox?: string;
        end_message?: string;
        steps?: {
          id: string;
          think: string;
          observation?: string;
          actions: {
            id: string;
            code: string;
            label: string;
            correct: boolean;
            result: string;
            consequence: string;
          }[];
        }[];
      };
      const steps = d.steps ?? [];
      const setSteps = (next: typeof steps) => set({ steps: next });
      return (
        <>
          <Field label="Title (optional)">
            <input value={d.title ?? ""} onChange={(e) => set({ title: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Goal">
            <textarea value={d.goal ?? ""} onChange={(e) => set({ goal: e.target.value })} rows={2} className={textareaClass} />
          </Field>
          <Field label="Inbox email">
            <textarea value={d.inbox ?? ""} onChange={(e) => set({ inbox: e.target.value })} rows={3} className={textareaClass} />
          </Field>
          <Field label='End message (include {score} for the first-try count)'>
            <textarea value={d.end_message ?? ""} onChange={(e) => set({ end_message: e.target.value })} rows={3} className={textareaClass} />
          </Field>
          <Field label="Steps">
            <div className="space-y-3">
              {steps.map((step, i) => (
                <div key={step.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-400">Step {i + 1}</span>
                    <RemoveBtn onClick={() => setSteps(steps.filter((_, xi) => xi !== i))} disabled={steps.length <= 2} />
                  </div>
                  <textarea value={step.think} onChange={(e) => setSteps(steps.map((x, xi) => xi === i ? { ...x, think: e.target.value } : x))} rows={2} className={textareaClass} placeholder="Think prompt" />
                  <input value={step.observation ?? ""} onChange={(e) => setSteps(steps.map((x, xi) => xi === i ? { ...x, observation: e.target.value } : x))} className={inputClass} placeholder="Observation (optional)" />
                  {step.actions.map((action, ai) => (
                    <div key={action.id} className="space-y-1 rounded border border-slate-100 p-2">
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="radio"
                            name={`correct-${step.id}`}
                            checked={action.correct}
                            onChange={() =>
                              setSteps(
                                steps.map((x, xi) =>
                                  xi === i
                                    ? {
                                        ...x,
                                        actions: x.actions.map((a, aj) => ({
                                          ...a,
                                          correct: aj === ai,
                                        })),
                                      }
                                    : x,
                                ),
                              )
                            }
                          />
                          correct
                        </label>
                        <RemoveBtn
                          onClick={() =>
                            setSteps(
                              steps.map((x, xi) =>
                                xi === i
                                  ? { ...x, actions: x.actions.filter((_, aj) => aj !== ai) }
                                  : x,
                              ),
                            )
                          }
                          disabled={step.actions.length <= 2}
                        />
                      </div>
                      <input value={action.code} onChange={(e) => setSteps(steps.map((x, xi) => xi === i ? { ...x, actions: x.actions.map((a, aj) => aj === ai ? { ...a, code: e.target.value } : a) } : x))} className={inputClass} placeholder="code e.g. classify_email()" />
                      <input value={action.label} onChange={(e) => setSteps(steps.map((x, xi) => xi === i ? { ...x, actions: x.actions.map((a, aj) => aj === ai ? { ...a, label: e.target.value } : a) } : x))} className={inputClass} placeholder="Button label" />
                      <input value={action.result} onChange={(e) => setSteps(steps.map((x, xi) => xi === i ? { ...x, actions: x.actions.map((a, aj) => aj === ai ? { ...a, result: e.target.value } : a) } : x))} className={inputClass} placeholder="Result if correct" />
                      <input value={action.consequence} onChange={(e) => setSteps(steps.map((x, xi) => xi === i ? { ...x, actions: x.actions.map((a, aj) => aj === ai ? { ...a, consequence: e.target.value } : a) } : x))} className={inputClass} placeholder="Consequence if wrong" />
                    </div>
                  ))}
                  <AddBtn
                    label="Add action"
                    onClick={() =>
                      setSteps(
                        steps.map((x, xi) =>
                          xi === i
                            ? {
                                ...x,
                                actions: [
                                  ...x.actions,
                                  {
                                    id: crypto.randomUUID(),
                                    code: "",
                                    label: "",
                                    correct: false,
                                    result: "—",
                                    consequence: "",
                                  },
                                ],
                              }
                            : x,
                        ),
                      )
                    }
                  />
                </div>
              ))}
              <AddBtn
                label="Add step"
                onClick={() =>
                  setSteps([
                    ...steps,
                    {
                      id: crypto.randomUUID(),
                      think: "",
                      actions: [
                        {
                          id: crypto.randomUUID(),
                          code: "",
                          label: "",
                          correct: true,
                          result: "",
                          consequence: "—",
                        },
                        {
                          id: crypto.randomUUID(),
                          code: "",
                          label: "",
                          correct: false,
                          result: "—",
                          consequence: "",
                        },
                      ],
                    },
                  ])
                }
              />
            </div>
          </Field>
        </>
      );
    }

    case "workflow_simulator": {
      const d = block.data as {
        title?: string;
        subtitle?: string;
        action_label?: string;
        perfect_verdict?: string;
        imperfect_verdict?: string;
        slots?: {
          id: string;
          emoji: string;
          label: string;
          options: {
            id: string;
            label: string;
            score: 0 | 1 | 2;
            issue?: string;
            result?: { text: string; ok?: boolean };
            results_by_type?: Record<string, { text: string; ok?: boolean }>;
          }[];
          blocked_by?: string;
          blocked_result?: string;
        }[];
        tests?: { id: string; message: string; type: string }[];
      };
      const slots = d.slots ?? [];
      const tests = d.tests ?? [];
      const setSlots = (next: typeof slots) => set({ slots: next });
      const setTests = (next: typeof tests) => set({ tests: next });
      type Opt = (typeof slots)[number]["options"][number];
      const setSlot = (i: number, patch: Partial<(typeof slots)[number]>) => setSlots(slots.map((x, xi) => (xi === i ? { ...x, ...patch } : x)));
      const setOpt = (i: number, oi: number, patch: Partial<Opt>) =>
        setSlots(slots.map((x, xi) => (xi === i ? { ...x, options: x.options.map((o, oj) => (oj === oi ? { ...o, ...patch } : o)) } : x)));
      const testTypes = [...new Set(tests.map((t) => t.type.trim()).filter(Boolean))];
      const okValue = (ok?: boolean) => (ok === undefined ? "" : ok ? "good" : "bad");
      const okFrom = (v: string) => (v === "" ? undefined : v === "good");
      return (
        <>
          <Field label="Title (optional)">
            <input value={d.title ?? ""} onChange={(e) => set({ title: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Subtitle (optional)">
            <textarea value={d.subtitle ?? ""} onChange={(e) => set({ subtitle: e.target.value })} rows={2} className={textareaClass} />
          </Field>
          <Field label="Final action label (optional)">
            <input value={d.action_label ?? ""} onChange={(e) => set({ action_label: e.target.value || undefined })} className={inputClass} />
          </Field>
          <Field label="Slots">
            <div className="space-y-3">
              {slots.map((slot, i) => (
                <div key={slot.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <input value={slot.emoji} onChange={(e) => setSlots(slots.map((x, xi) => (xi === i ? { ...x, emoji: e.target.value } : x)))} className={`${inputClass} w-16`} />
                    <input value={slot.label} onChange={(e) => setSlots(slots.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder="Slot label" />
                    <RemoveBtn
                      onClick={() =>
                        setSlots(
                          slots
                            .filter((_, xi) => xi !== i)
                            .map((x) => (x.blocked_by === slot.id ? { ...x, blocked_by: undefined, blocked_result: undefined } : x)),
                        )
                      }
                      disabled={slots.length <= 2}
                    />
                  </div>
                  {slot.options.map((opt, oi) => (
                    <div key={opt.id} className="space-y-1 rounded border border-slate-100 p-2">
                      <div className="flex items-center gap-2">
                        <input value={opt.label} onChange={(e) => setSlots(slots.map((x, xi) => xi === i ? { ...x, options: x.options.map((o, oj) => oj === oi ? { ...o, label: e.target.value } : o) } : x))} className={inputClass} placeholder="Option label" />
                        <select value={opt.score} onChange={(e) => setSlots(slots.map((x, xi) => xi === i ? { ...x, options: x.options.map((o, oj) => oj === oi ? { ...o, score: Number(e.target.value) as 0 | 1 | 2 } : o) } : x))} className={`${inputClass} w-24`}>
                          <option value={2}>Best (2)</option>
                          <option value={1}>Ok (1)</option>
                          <option value={0}>Bad (0)</option>
                        </select>
                        <RemoveBtn onClick={() => setSlots(slots.map((x, xi) => xi === i ? { ...x, options: x.options.filter((_, oj) => oj !== oi) } : x))} disabled={slot.options.length <= 2} />
                      </div>
                      {opt.score < 2 ? (
                        <input value={opt.issue ?? ""} onChange={(e) => setSlots(slots.map((x, xi) => xi === i ? { ...x, options: x.options.map((o, oj) => oj === oi ? { ...o, issue: e.target.value } : o) } : x))} className={inputClass} placeholder="Issue text when not best" />
                      ) : null}
                      <div className="flex items-center gap-2">
                        <input
                          value={opt.result?.text ?? ""}
                          onChange={(e) => setOpt(i, oi, { result: e.target.value ? { ...opt.result, text: e.target.value } : undefined })}
                          className={inputClass}
                          placeholder="Run-log line when picked, e.g. ⚡ Triggered straight away. {type} = the test's type"
                        />
                        <select
                          value={okValue(opt.result?.ok)}
                          disabled={!opt.result}
                          onChange={(e) => opt.result && setOpt(i, oi, { result: { ...opt.result, ok: okFrom(e.target.value) } })}
                          className={`${inputClass} w-40`}
                          aria-label="Shown as"
                        >
                          <option value="">Good only if best</option>
                          <option value="good">Shown as good</option>
                          <option value="bad">Shown as bad</option>
                        </select>
                      </div>
                      {testTypes.map((type) => {
                        const line = opt.results_by_type?.[type];
                        const setLine = (next?: { text: string; ok?: boolean }) => {
                          const rest = { ...(opt.results_by_type ?? {}) };
                          if (next) rest[type] = next;
                          else delete rest[type];
                          setOpt(i, oi, { results_by_type: Object.keys(rest).length ? rest : undefined });
                        };
                        return (
                          <div key={type} className="flex items-center gap-2">
                            <input
                              value={line?.text ?? ""}
                              onChange={(e) => setLine(e.target.value ? { ...line, text: e.target.value } : undefined)}
                              className={inputClass}
                              placeholder={`Different line for “${type}” tests (optional)`}
                            />
                            <select value={okValue(line?.ok)} disabled={!line} onChange={(e) => line && setLine({ ...line, ok: okFrom(e.target.value) })} className={`${inputClass} w-40`} aria-label="Shown as">
                              <option value="">Good only if best</option>
                              <option value="good">Shown as good</option>
                              <option value="bad">Shown as bad</option>
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {i > 0 ? (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                      <span>If this step&apos;s input comes from</span>
                      <select value={slot.blocked_by ?? ""} onChange={(e) => setSlot(i, { blocked_by: e.target.value || undefined })} className={`${inputClass} w-40`} aria-label="Depends on">
                        <option value="">no earlier step</option>
                        {slots.slice(0, i).map((x) => (
                          <option key={x.id} value={x.id}>{x.label || "Untitled step"}</option>
                        ))}
                      </select>
                      {slot.blocked_by ? (
                        <input value={slot.blocked_result ?? ""} onChange={(e) => setSlot(i, { blocked_result: e.target.value || undefined })} className={`${inputClass} min-w-[14rem] flex-1`} placeholder="Line shown when that step isn't the best choice" />
                      ) : null}
                    </div>
                  ) : null}
                  <AddBtn label="Add option" onClick={() => setSlots(slots.map((x, xi) => xi === i ? { ...x, options: [...x.options, { id: crypto.randomUUID(), label: "", score: 0 as const, issue: "" }] } : x))} />
                </div>
              ))}
              {slots.length < 6 ? (
                <AddBtn label="Add slot" onClick={() => setSlots([...slots, { id: crypto.randomUUID(), emoji: "•", label: "", options: [{ id: crypto.randomUUID(), label: "", score: 2 }, { id: crypto.randomUUID(), label: "", score: 0, issue: "" }] }])} />
              ) : null}
            </div>
          </Field>
          <Field label="Test responses">
            <div className="space-y-2">
              {tests.map((test, i) => (
                <div key={test.id} className="flex flex-col gap-2 rounded-md border border-slate-200 p-2 sm:flex-row">
                  <input value={test.message} onChange={(e) => setTests(tests.map((x, xi) => xi === i ? { ...x, message: e.target.value } : x))} className={inputClass} placeholder="Message shown in the run" />
                  <input value={test.type} onChange={(e) => setTests(tests.map((x, xi) => xi === i ? { ...x, type: e.target.value } : x))} className={`${inputClass} sm:w-32`} placeholder="type e.g. praise" />
                  <RemoveBtn onClick={() => setTests(tests.filter((_, xi) => xi !== i))} disabled={tests.length <= 1} />
                </div>
              ))}
              <AddBtn label="Add test" onClick={() => setTests([...tests, { id: crypto.randomUUID(), message: "", type: "" }])} />
            </div>
          </Field>
          <Field label="Perfect verdict">
            <textarea value={d.perfect_verdict ?? ""} onChange={(e) => set({ perfect_verdict: e.target.value })} rows={2} className={textareaClass} />
          </Field>
          <Field label="Imperfect verdict heading">
            <input value={d.imperfect_verdict ?? ""} onChange={(e) => set({ imperfect_verdict: e.target.value })} className={inputClass} />
          </Field>
        </>
      );
    }

    case "be_the_model": {
      const d = block.data as {
        title?: string;
        subtitle?: string;
        prompt?: string;
        temperature_min?: number;
        temperature_max?: number;
        temperature_default?: number;
        max_words?: number;
        min_sentences?: number;
        insight_both_temps?: string;
        insight_hint?: string;
        transitions?: { after: string; options: { word: string; p: number }[] }[];
      };
      const transitions = d.transitions ?? [];
      const setTransitions = (next: typeof transitions) => set({ transitions: next });
      return (
        <>
          <Field label="Title (optional)">
            <input value={d.title ?? ""} onChange={(e) => set({ title: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Subtitle (optional)">
            <input value={d.subtitle ?? ""} onChange={(e) => set({ subtitle: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Prompt stem">
            <input
              value={d.prompt ?? ""}
              onChange={(e) => set({ prompt: e.target.value })}
              className={inputClass}
              placeholder="My favourite thing to eat for breakfast is"
            />
          </Field>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Field label="Temp min">
              <input type="number" step={0.1} min={0.05} max={2} value={d.temperature_min ?? 0.1} onChange={(e) => set({ temperature_min: Number(e.target.value) })} className={inputClass} />
            </Field>
            <Field label="Temp max">
              <input type="number" step={0.1} min={0.05} max={2} value={d.temperature_max ?? 2} onChange={(e) => set({ temperature_max: Number(e.target.value) })} className={inputClass} />
            </Field>
            <Field label="Temp default">
              <input type="number" step={0.1} min={0.05} max={2} value={d.temperature_default ?? 1} onChange={(e) => set({ temperature_default: Number(e.target.value) })} className={inputClass} />
            </Field>
            <Field label="Min sentences">
              <input type="number" min={1} max={10} value={d.min_sentences ?? 3} onChange={(e) => set({ min_sentences: Number(e.target.value) })} className={inputClass} />
            </Field>
          </div>
          <Field label="Transitions (after → next words)">
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Use <code className="rounded bg-slate-100 px-1">START</code> for the first word. Use{" "}
                <code className="rounded bg-slate-100 px-1">.</code> as a word to end the sentence.
              </p>
              {transitions.map((tr, i) => (
                <div key={i} className="space-y-2 rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <input
                      value={tr.after}
                      onChange={(e) =>
                        setTransitions(
                          transitions.map((x, xi) => (xi === i ? { ...x, after: e.target.value } : x)),
                        )
                      }
                      className={`${inputClass} w-40`}
                      placeholder="START or previous word"
                    />
                    <RemoveBtn onClick={() => setTransitions(transitions.filter((_, xi) => xi !== i))} />
                  </div>
                  {tr.options.map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2 ps-2">
                      <input
                        value={opt.word}
                        onChange={(e) =>
                          setTransitions(
                            transitions.map((x, xi) =>
                              xi === i
                                ? {
                                    ...x,
                                    options: x.options.map((o, oj) =>
                                      oj === oi ? { ...o, word: e.target.value } : o,
                                    ),
                                  }
                                : x,
                            ),
                          )
                        }
                        className={inputClass}
                        placeholder="word"
                      />
                      <input
                        type="number"
                        step={0.01}
                        min={0.01}
                        max={1}
                        value={opt.p}
                        onChange={(e) =>
                          setTransitions(
                            transitions.map((x, xi) =>
                              xi === i
                                ? {
                                    ...x,
                                    options: x.options.map((o, oj) =>
                                      oj === oi ? { ...o, p: Number(e.target.value) } : o,
                                    ),
                                  }
                                : x,
                            ),
                          )
                        }
                        className={`${inputClass} w-20`}
                      />
                      <RemoveBtn
                        onClick={() =>
                          setTransitions(
                            transitions.map((x, xi) =>
                              xi === i
                                ? { ...x, options: x.options.filter((_, oj) => oj !== oi) }
                                : x,
                            ),
                          )
                        }
                        disabled={tr.options.length <= 1}
                      />
                    </div>
                  ))}
                  <AddBtn
                    label="Add option"
                    onClick={() =>
                      setTransitions(
                        transitions.map((x, xi) =>
                          xi === i
                            ? { ...x, options: [...x.options, { word: "", p: 0.1 }] }
                            : x,
                        ),
                      )
                    }
                  />
                </div>
              ))}
              <AddBtn
                label="Add transition"
                onClick={() =>
                  setTransitions([...transitions, { after: "", options: [{ word: "", p: 0.5 }] }])
                }
              />
            </div>
          </Field>
          <Field label="Insight when both temp ends tried">
            <textarea value={d.insight_both_temps ?? ""} onChange={(e) => set({ insight_both_temps: e.target.value })} rows={2} className={textareaClass} />
          </Field>
          <Field label="Insight hint otherwise">
            <textarea value={d.insight_hint ?? ""} onChange={(e) => set({ insight_hint: e.target.value })} rows={2} className={textareaClass} />
          </Field>
        </>
      );
    }

    case "tokenizer_playground": {
      const d = block.data as {
        title?: string;
        min_distinct?: number;
        fine_print?: string;
        examples?: { id: string; label: string; text: string; tokens?: string[] }[];
      };
      const examples = d.examples ?? [];
      const setExamples = (next: typeof examples) => set({ examples: next });
      return (
        <>
          <Field label="Title (optional)">
            <input
              value={d.title ?? ""}
              onChange={(e) => set({ title: e.target.value })}
              className={inputClass}
              placeholder="e.g. Tokenizer playground"
            />
          </Field>
          <Field label="Examples to open (besides the first) to complete">
            <input
              type="number"
              min={1}
              max={10}
              value={d.min_distinct ?? 2}
              onChange={(e) => set({ min_distinct: Number(e.target.value) })}
              className={`${inputClass} w-24`}
            />
          </Field>
          <FieldGroup label="Examples (the first is shown on open)">
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                Each example&apos;s token split is worked out ahead of time with GPT-4o&apos;s tokenizer, so its
                text can&apos;t be edited here. Ask a developer to add or change an example.
              </p>
              {examples.map((ex, i) => (
                <div key={ex.id} className="space-y-1 rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <input
                      value={ex.label}
                      onChange={(e) => setExamples(examples.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))}
                      className={inputClass}
                      placeholder="Button label"
                      aria-label="Button label"
                    />
                    <RemoveBtn onClick={() => setExamples(examples.filter((_, xi) => xi !== i))} disabled={examples.length <= 2} />
                  </div>
                  <p className="text-sm text-slate-700" dir="auto">
                    {ex.text} <span className="text-xs text-slate-400">· {ex.tokens?.length ?? 0} tokens</span>
                  </p>
                </div>
              ))}
            </div>
          </FieldGroup>
          <Field label="Fine print (optional)">
            <textarea
              value={d.fine_print ?? ""}
              onChange={(e) => set({ fine_print: e.target.value })}
              rows={2}
              className={textareaClass}
            />
          </Field>
        </>
      );
    }

    case "cliche_highlighter": {
      const d = block.data as {
        title?: string;
        sample_text?: string;
        phrases?: string[];
      };
      const text = d.sample_text ?? "";
      const phrases = d.phrases ?? [];
      const missing = new Set(missingPhrases(text, phrases));
      const matches = findPhrases(text, phrases);
      return (
        <>
          <Field label="Title (optional)">
            <input
              value={d.title ?? ""}
              onChange={(e) => set({ title: e.target.value })}
              className={inputClass}
              placeholder="e.g. Keep your voice"
            />
          </Field>
          <Field label="Passage (learners reveal its common AI phrases)">
            <textarea
              value={text}
              onChange={(e) => set({ sample_text: e.target.value })}
              rows={4}
              className={textareaClass}
              placeholder="A paragraph full of AI clichés for the learner to try…"
            />
          </Field>
          <FieldGroup label="Phrases to highlight">
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                Type each phrase exactly as it appears in the passage. Capital letters don&apos;t matter.
              </p>
              {phrases.map((p, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <input
                      value={p}
                      onChange={(e) => set({ phrases: phrases.map((x, xi) => (xi === i ? e.target.value : x)) })}
                      className={inputClass}
                      placeholder="e.g. thrilled to announce"
                    />
                    <RemoveBtn onClick={() => set({ phrases: phrases.filter((_, xi) => xi !== i) })} />
                  </div>
                  {missing.has(p) ? <p className="text-xs text-amber-700">Not in the passage, so it won&apos;t highlight.</p> : null}
                </div>
              ))}
              {phrases.length < 40 ? <AddBtn label="Add phrase" onClick={() => set({ phrases: [...phrases, ""] })} /> : null}
              {text.trim() && matches.length ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-relaxed text-slate-700">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">What learners will see highlighted</span>
                  {matches.map((m, i) => (
                    <span key={m.start}>
                      {text.slice(i ? matches[i - 1]!.end : 0, m.start)}
                      <mark className="rounded bg-amber-200 px-0.5 text-amber-950">{m.text}</mark>
                    </span>
                  ))}
                  {text.slice(matches[matches.length - 1]!.end)}
                </div>
              ) : null}
            </div>
          </FieldGroup>
        </>
      );
    }

    case "context_window_slider": {
      const d = block.data as {
        title?: string;
        window_min?: number;
        messages?: { id: string; role: "you" | "ai"; text: string }[];
        remembered_answer?: string;
        forgotten_answer?: string;
      };
      const messages = d.messages ?? [];
      const setMessages = (next: typeof messages) => set({ messages: next });
      return (
        <>
          <Field label="Title (optional)">
            <input
              value={d.title ?? ""}
              onChange={(e) => set({ title: e.target.value })}
              className={inputClass}
              placeholder="e.g. Shrink the window"
            />
          </Field>
          <Field label="Minimum window size">
            <input
              type="number"
              min={1}
              max={Math.max(1, messages.length - 1)}
              value={d.window_min ?? 2}
              onChange={(e) => set({ window_min: Number(e.target.value) })}
              className={`${inputClass} w-24`}
            />
          </Field>
          <Field label="Chat messages (3–20)">
            <div className="space-y-2">
              {messages.map((m, i) => (
                <div key={m.id} className="space-y-2 rounded-md border border-slate-200 p-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={m.role}
                      onChange={(e) =>
                        setMessages(
                          messages.map((x, xi) =>
                            xi === i ? { ...x, role: e.target.value as "you" | "ai" } : x,
                          ),
                        )
                      }
                      className={`${inputClass} w-28`}
                    >
                      <option value="you">You</option>
                      <option value="ai">AI</option>
                    </select>
                    <RemoveBtn
                      onClick={() => setMessages(messages.filter((_, xi) => xi !== i))}
                      disabled={messages.length <= 3}
                    />
                  </div>
                  <textarea
                    value={m.text}
                    onChange={(e) =>
                      setMessages(
                        messages.map((x, xi) => (xi === i ? { ...x, text: e.target.value } : x)),
                      )
                    }
                    rows={2}
                    className={textareaClass}
                    placeholder="Message text"
                  />
                </div>
              ))}
              {messages.length < 20 ? (
                <AddBtn
                  label="Add message"
                  onClick={() =>
                    setMessages([
                      ...messages,
                      { id: crypto.randomUUID(), role: "you", text: "" },
                    ])
                  }
                />
              ) : null}
            </div>
          </Field>
          <Field label="Answer when name is still in the window">
            <textarea
              value={d.remembered_answer ?? ""}
              onChange={(e) => set({ remembered_answer: e.target.value })}
              rows={2}
              className={textareaClass}
            />
          </Field>
          <Field label="Answer when the first message has fallen out">
            <textarea
              value={d.forgotten_answer ?? ""}
              onChange={(e) => set({ forgotten_answer: e.target.value })}
              rows={2}
              className={textareaClass}
            />
          </Field>
        </>
      );
    }

    case "workplace_scenario": {
      const d = block.data as {
        component_prompt?: string;
        metadata_fields?: string[];
      };
      // The metadata axes an author can reference in the prompt (course_metadata types).
      // Values are filled per learner at generation. metadata_fields stays the full set
      // so the generator always has the learner's context available.
      const VARIABLES = ["domain", "department", "future_goal"];
      const prompt = d.component_prompt ?? "";
      const insertToken = (f: string) => {
        const token = `{{${f}}}`;
        const next = prompt ? `${prompt}${prompt.endsWith(" ") ? "" : " "}${token}` : token;
        set({ component_prompt: next, metadata_fields: VARIABLES });
      };
      return (
        <>
          <p className="mb-2 rounded-md bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
            <strong>The learner&apos;s values are always sent to the model alongside your prompt</strong>
            {" "}&mdash; you do not have to mention them for the scenario to be personalized. Write what
            the scenario should <em>do</em> (&ldquo;give them a plausible role for their department&rdquo;)
            and the model reads the values from that context. One scenario is generated per learner
            group, not per learner.
          </p>
          <p className="mb-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
            The tokens below are a separate, optional thing: use one only when you want the value to
            appear <em>inline</em> in a sentence. A variable this course defines no values for is left
            out of the context entirely &mdash; it is never sent as &ldquo;none&rdquo; &mdash; so do not
            ask the prompt to use one the course does not collect, or the model will invent it.
          </p>
          <Field label="Generation prompt">
            <textarea
              value={prompt}
              onChange={(e) => set({ component_prompt: e.target.value })}
              rows={5}
              placeholder="e.g. Write a short, realistic workplace scenario (130-170 words) about a professional in this learner's field. Use their domain and department from the context to give them a plausible role. Third person, plain language, no headings."
              className={inputClass}
            />
          </Field>
          <Field label="Insert a value inline (optional — the model already has all of these)">
            <div className="flex flex-wrap gap-2">
              {VARIABLES.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => insertToken(f)}
                  title={`Insert {{${f}}} where you want the learner's ${f.replace("_", " ")} to appear inline`}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-800"
                >
                  {"{{"}{f}{"}}"}
                </button>
              ))}
            </div>
          </Field>
        </>
      );
    }

    case "scenario_sort":
      return <ScenarioSortEditor data={block.data} set={set} />;
    case "spot_the_errors":
      return <SpotTheErrorsEditor data={block.data} set={set} />;
    case "decision_tree":
      return <DecisionTreeEditor data={block.data} set={set} />;
    case "prompt_builder":
      return <PromptBuilderEditor data={block.data} set={set} />;

    case "quick_question":
    case "card_grid":
    case "compare_columns":
    case "tile_reveal":
    case "color_sort":
    case "slider_set":
    case "project_export":
    case "filter_compare":
    case "train_model":
    case "meaning_map":
      return <JsonDataField data={block.data} onChange={(next) => replaceData(next)} />;

    default:
      return (
        <p className="text-xs text-slate-500">
          No editor for “{BLOCK_LABELS[block.type as BlockType]}” yet — coming in a later slice.
        </p>
      );
  }
}

/** A colour for a slice or layer; empty keeps the block's automatic colour. */
/**
 * The simulators' data is mostly lists of words, weights and positions, so it is edited as
 * JSON. The text only replaces the block's data once it parses; the lesson's save still
 * checks it against the schema.
 */
/**
 * Comma-separated choices for a form field. Keeps the raw text while the author types, so a
 * first choice or a trailing comma doesn't vanish; two or more choices make it a choice field.
 */
function ChoicesInput({ options, onChange }: { options?: string[]; onChange: (next: string[] | undefined) => void }) {
  const [text, setText] = useState(() => (options ?? []).join(", "));
  return (
    <input
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const next = e.target.value.split(",").map((o) => o.trim()).filter(Boolean);
        onChange(next.length >= 2 ? next : undefined);
      }}
      className={inputClass}
      placeholder="Choices (optional, comma-separated): learners pick one instead of typing"
    />
  );
}

function JsonDataField({
  data,
  onChange,
  label = "Block data (JSON)",
  rows = 18,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  label?: string;
  rows?: number;
}) {
  const [text, setText] = useState(() => JSON.stringify(data, null, 2));
  const [error, setError] = useState<string | null>(null);
  return (
    <Field label={label}>
      <textarea
        value={text}
        rows={rows}
        spellCheck={false}
        onChange={(e) => {
          setText(e.target.value);
          try {
            const parsed: unknown = JSON.parse(e.target.value);
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("must be a JSON object");
            setError(null);
            onChange(parsed as Record<string, unknown>);
          } catch (err) {
            setError(err instanceof Error ? err.message : "Invalid JSON");
          }
        }}
        className={textareaClass}
      />
      {error ? <span className="mt-1 block text-xs text-amber-700">Not saved yet: {error}</span> : null}
    </Field>
  );
}

function ImageUrlField({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const upload = async (file: File) => {
    setErr(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch("/api/admin/interactive/upload", { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Upload failed (${res.status})`);
      onChange(data.url as string);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or upload an image"
          className={inputClass}
        />
        <label className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          Upload
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload(f);
              e.target.value = "";
            }}
          />
        </label>
      </div>
      {value ? (
        <img src={value} alt="" className="max-h-24 rounded border border-slate-200" />
      ) : null}
      {err ? <p className="text-xs text-rose-600">{err}</p> : null}
    </div>
  );
}

const IMAGE_SIZE_LABELS: Record<ImageSize, string> = {
  small: "Small",
  medium: "Medium",
  large: "Large",
  full: "Full width",
};

const IMAGE_ALIGN_LABELS: Record<ImageAlign, string> = {
  left: "Left",
  center: "Centre",
  right: "Right",
};

function SegmentedControl<T extends string>({
  options,
  labels,
  value,
  onChange,
}: {
  options: readonly T[];
  labels: Record<T, string>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={value === option}
          className={`rounded px-2.5 py-1 text-xs font-semibold transition-colors ${
            value === option ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {labels[option]}
        </button>
      ))}
    </div>
  );
}
